import { HTMLAttributes, ReactNode } from 'react';
import { cn } from '@/utils/cn';

/**
 * Card padding presets
 */
type CardPadding = 'none' | 'sm' | 'md' | 'lg';

const PADDING_CLASSES: Record<CardPadding, string> = {
  none: '',
  sm: 'p-4',
  md: 'p-6',
  lg: 'p-8',
};

export interface CardProps extends Omit<HTMLAttributes<HTMLDivElement>, 'className'> {
  /** Card content */
  children: ReactNode;
  /** Additional classes appended to the base styles. Note: not guaranteed to override conflicting base utilities (cn is a concat, not tailwind-merge) — prefer additive classes. */
  className?: string;
  /** Padding preset (default 'md' = p-6) */
  padding?: CardPadding;
}

/**
 * Card - Generic white surface with border/shadow, matching the
 * admin list-table card styling (`bg-white rounded-lg border border-gray-200 shadow-sm`).
 *
 * @example
 * <Card>
 *   <p>Content</p>
 * </Card>
 *
 * @example
 * // Search/filter bar styling (padding sm, no shadow needed but included by default)
 * <Card padding="sm">
 *   <form>...</form>
 * </Card>
 */
export const Card = ({
  children,
  className = '',
  padding = 'md',
  ...props
}: CardProps) => {
  return (
    <div
      className={cn(
        'bg-white rounded-lg border border-gray-200 shadow-sm',
        PADDING_CLASSES[padding],
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
};

const CARD_HEADER_PADDING_CLASSES: Record<CardPadding, string> = {
  none: '',
  sm: 'px-4 py-3',
  md: 'px-6 py-4',
  lg: 'px-8 py-5',
};

export interface CardHeaderProps extends Omit<HTMLAttributes<HTMLDivElement>, 'className'> {
  children: ReactNode;
  className?: string;
  /** Padding preset (default 'md' = px-6 py-4) */
  padding?: CardPadding;
}

/**
 * CardHeader - Optional header slot for Card, matching the
 * `px-6 py-4 border-b border-gray-200 bg-gray-50` pattern used in detail pages.
 *
 * @example
 * <Card padding="none">
 *   <CardHeader><h2 className="text-lg font-semibold text-gray-900">Title</h2></CardHeader>
 *   <CardBody>...</CardBody>
 * </Card>
 *
 * @example
 * // Dense grid tiles (smaller padding)
 * <CardHeader padding="sm">...</CardHeader>
 */
export const CardHeader = ({ children, className = '', padding = 'md', ...props }: CardHeaderProps) => {
  return (
    <div
      className={cn(CARD_HEADER_PADDING_CLASSES[padding], 'border-b border-gray-200 bg-gray-50', className)}
      {...props}
    >
      {children}
    </div>
  );
};

export interface CardBodyProps extends Omit<HTMLAttributes<HTMLDivElement>, 'className'> {
  children: ReactNode;
  className?: string;
  /** Padding preset (default 'md' = p-6) */
  padding?: CardPadding;
}

/**
 * CardBody - Optional body slot for Card, matching the `p-6` content padding
 * used alongside CardHeader in detail pages.
 *
 * @example
 * // Dense grid tiles (smaller padding)
 * <CardBody padding="sm">...</CardBody>
 */
export const CardBody = ({ children, className = '', padding = 'md', ...props }: CardBodyProps) => {
  return (
    <div className={cn(PADDING_CLASSES[padding], className)} {...props}>
      {children}
    </div>
  );
};

export default Card;
